import { type ComponentProps, type FC, useMemo } from "react";

import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger } from "../../components";
import { cn } from "../../utils";
import { FIELD_BORDERLESS_CLASS_NAME, FIELD_READ_ONLY_CLASS_NAME } from "./field.constants";
import { FieldBase, type FieldBaseProps } from "./field-base";
import { useField } from "./use-field";

export interface FieldSelectOption {
  type?: "label" | "item";
  label: string;
  value?: string;
  description?: string;
  itemProps?: ComponentProps<typeof SelectItem>;
}

export type FieldSelectProps = FieldBaseProps & {
  placeholder?: string;
  borderless?: boolean;
  options: readonly FieldSelectOption[];
  onChange?: ComponentProps<typeof Select>["onValueChange"];
  onBlur?: ComponentProps<typeof SelectTrigger>["onBlur"];
  inputProps?: ComponentProps<typeof SelectTrigger>;
};

export const FieldSelect: FC<FieldSelectProps> = ({
  placeholder = "Select an option",
  borderless,
  readOnly,
  options,
  onChange,
  onBlur,
  inputProps,
  ...props
}) => {
  const { field, hasError } = useField({ name: props.name, disabled: props.disabled, inputProps });

  const handleChange: FieldSelectProps["onChange"] = (value) => {
    field.onChange(value);
    onChange?.(value);
  };

  const handleBlur: FieldSelectProps["onBlur"] = (event) => {
    field.onBlur();
    onBlur?.(event);
  };

  const selectedOption = useMemo(() => options.find((option) => option.value === field.value), [options, field.value]);

  return (
    <FieldBase {...props}>
      <Select onValueChange={handleChange} name={field.name} value={field.value}>
        <SelectTrigger
          {...inputProps}
          ref={field.ref}
          onBlur={handleBlur}
          disabled={field.disabled}
          className={cn("text-left! bg-background", {
            "border-destructive": hasError && !field.disabled,
            [FIELD_BORDERLESS_CLASS_NAME]: borderless,
            [FIELD_READ_ONLY_CLASS_NAME]: readOnly,
          })}
        >
          <div className={cn("max-w-full truncate", { "text-muted-foreground": !selectedOption })}>
            {selectedOption?.label || placeholder}
          </div>
        </SelectTrigger>

        {options.length > 0 && (
          <SelectContent className="max-w-[calc(var(--radix-select-trigger-width)+2px)]">
            <SelectGroup>
              {options.map((option) => {
                if (option.type === "label") {
                  return (
                    <SelectLabel key={option.label} {...option.itemProps}>
                      {option.label}
                    </SelectLabel>
                  );
                }

                if (!option.value) {
                  return null;
                }

                return (
                  <SelectItem key={option.value} {...option.itemProps} value={option.value}>
                    <div className={cn("flex flex-col gap-0.5", { "pt-1 pb-1": !!option.description })}>
                      <div className={cn({ "font-medium": option.description })}>{option.label}</div>
                      {option.description && (
                        <span className="text-sm text-muted-foreground">{option.description}</span>
                      )}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectGroup>
          </SelectContent>
        )}
      </Select>
    </FieldBase>
  );
};
